import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import Icon from "@/components/icon";
import Section from "@/components/section";
import { RowDivider } from "@/components/row";
import StickyActionButton from "@/components/sticky-action-button";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import DatePickerDrawer from "@/components/datepicker/date-picker-drawer";
import { UserService } from "@/services/user-service";
import { useProfileStore } from "@/store/profile-store";
import type { City, State, User } from "@/models/auth/User";
import statesData from "@/constants/states.json";
import citiesData from "@/constants/cities.json";

export const USER_INFO_QUERY_KEY = ["user-info"] as const;

const GENDER_OPTIONS = [
  { value: 1, label: "مرد", icon: "male" },
  { value: 2, label: "زن", icon: "female" },
] as const;


// Bordered input — used inside the picker drawers (search box).
const inputClass =
  "w-full h-11 rounded-lg border border-default-100 bg-background px-3 text-sm text-default-800 placeholder:text-default-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/70";

// Borderless field controls — the Telegram card provides the surface/divider.
export const fieldInputClass =
  "w-full h-10 bg-transparent text-sm text-default-800 placeholder:text-default-400 focus:outline-none";
export const pickerButtonClass =
  "w-full h-10 bg-transparent text-sm text-default-800 flex items-center justify-between disabled:opacity-60";

export function toLatinDigits(value: string, max = Infinity) {
  return Array.from(value)
    .map((c) => {
      const code = c.charCodeAt(0);
      if (code >= 1776 && code <= 1785) return String(code - 1776);
      if (code >= 1632 && code <= 1641) return String(code - 1632);
      return c;
    })
    .join("")
    .replace(/\D/g, "")
    .slice(0, max);
}

export function isValidNationalCode(code: string) {
  if (!/^\d{10}$/.test(code)) return false;
  if (/^(\d)\1{9}$/.test(code)) return false;
  const digits = code.split("").map(Number);
  const sum = digits.slice(0, 9).reduce((acc, d, i) => acc + d * (10 - i), 0);
  const remainder = sum % 11;
  const check = digits[9];
  return remainder < 2 ? check === remainder : check === 11 - remainder;
}

type Props = {
  /** Seed values. Falls back to the profile store when omitted. */
  user?: User | null;
  submitLabel?: string;
  savingLabel?: string;
  onSaved?: (user: User) => void;
  /** Content shown above the form (avatar, banner, etc.). */
  headerSlot?: ReactNode;
  /** Layout: "inline" keeps the submit button in-flow; "sticky" pins it to bottom. */
  submitLayout?: "inline" | "sticky";
};

export default function ProfileEditForm({
  user,
  submitLabel = "ذخیره اطلاعات",
  savingLabel = "در حال ذخیره...",
  onSaved,
  headerSlot,
  submitLayout = "sticky",
}: Props) {
  const queryClient = useQueryClient();
  const storeProfile = useProfileStore((s) => s.profile);
  const setProfile = useProfileStore((s) => s.setProfile);
  const seed = user ?? storeProfile;

  const states = statesData as State[];
  const cities = citiesData as City[];

  const [displayName, setDisplayName] = useState(seed?.display_name || "");
  const [stateId, setStateId] = useState(seed?.state?.id || 0);
  const [cityId, setCityId] = useState(seed?.city?.id || 0);
  const [birthdate, setBirthdate] = useState(seed?.birthdate || "");
  const [gender, setGender] = useState<number>(seed?.gender || 1);
  const [nationalityCode, setNationalityCode] = useState(
    seed?.nationality_code || "",
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const [stateOpen, setStateOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");

  // Re-seed when the canonical user value changes (e.g. server refresh).
  useEffect(() => {
    if (!seed) return;
    setDisplayName(seed.display_name || "");
    setStateId(seed.state?.id || 0);
    setCityId(seed.city?.id || 0);
    setBirthdate(seed.birthdate || "");
    setGender(seed.gender || 1);
    setNationalityCode(seed.nationality_code || "");
  }, [seed]);

  useEffect(() => {
    if (!cityId) return;
    const stillValid = cities.some(
      (c) => c.id === cityId && c.state_id === stateId,
    );
    if (!stillValid) setCityId(0);
  }, [stateId, cityId, cities]);

  const selectedState = useMemo(
    () => states.find((s) => s.id === stateId),
    [states, stateId],
  );
  const selectedCity = useMemo(
    () => cities.find((c) => c.id === cityId),
    [cities, cityId],
  );
  const stateCities = useMemo(
    () => cities.filter((c) => c.state_id === stateId),
    [cities, stateId],
  );

  const filteredStates = useMemo(() => {
    const term = stateSearch.trim();
    return term ? states.filter((s) => s.name.includes(term)) : states;
  }, [states, stateSearch]);
  const filteredCities = useMemo(() => {
    const term = citySearch.trim();
    return term ? stateCities.filter((c) => c.name.includes(term)) : stateCities;
  }, [stateCities, citySearch]);

  const validate = () => {
    const next: Record<string, string> = {};
    if (!displayName.trim()) next.display_name = "نام و نام خانوادگی الزامی است";
    if (!stateId) next.state_id = "انتخاب استان الزامی است";
    if (!cityId) next.city_id = "انتخاب شهر الزامی است";
    if (!gender) next.gender = "انتخاب جنسیت الزامی است";
    if (nationalityCode.trim() && !isValidNationalCode(nationalityCode)) {
      next.nationality_code = "کد ملی وارد شده معتبر نیست";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async () => {
    if (!validate() || saving) return;
    setSaving(true);
    try {
      const res = await UserService.updateCurrentUser({
        display_name: displayName.trim(),
        state_id: stateId,
        city_id: cityId,
        birthdate: birthdate.trim() || null,
        gender,
        nationality_code: nationalityCode,
      });
      if (!res.success || !res.data) {
        toast.error(res.message || "خطا در ذخیره اطلاعات");
        return;
      }
      queryClient.setQueryData(USER_INFO_QUERY_KEY, res);
      const merged = { ...(storeProfile ?? ({} as User)), ...res.data };
      setProfile(merged);
      toast.success("اطلاعات ذخیره شد");
      onSaved?.(res.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {headerSlot}

      <Section title="اطلاعات شخصی">
        <Field label="نام و نام خانوادگی" error={errors.display_name}>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="نام و نام خانوادگی"
            className={fieldInputClass}
          />
        </Field>
        {seed?.phone ? (
          <>
            <RowDivider />
            <Field label="شماره همراه">
              <div className="flex h-10 items-center justify-between">
                <span className="text-sm text-default-500" dir="ltr">
                  {seed.phone}
                </span>
                <Icon name="lock" size={15} className="text-default-300" />
              </div>
            </Field>
          </>
        ) : null}
        <RowDivider />
        <Field label="کد ملی (اختیاری)" error={errors.nationality_code}>
          <input
            value={nationalityCode}
            onChange={(e) => setNationalityCode(toLatinDigits(e.target.value, 10))}
            placeholder="کد ملی"
            inputMode="numeric"
            dir="ltr"
            className={fieldInputClass}
          />
        </Field>
      </Section>

      <Section title="محل سکونت">
        <Field label="استان" error={errors.state_id}>
          <button
            type="button"
            onClick={() => {
              setStateSearch("");
              setStateOpen(true);
            }}
            className={pickerButtonClass}
          >
            <span>{selectedState?.name ?? "انتخاب استان"}</span>
            <Icon name="chevron-left" size={18} className="text-default-400" />
          </button>
        </Field>
        <RowDivider />
        <Field label="شهر" error={errors.city_id}>
          <button
            type="button"
            disabled={!stateId || stateCities.length === 0}
            onClick={() => {
              setCitySearch("");
              setCityOpen(true);
            }}
            className={pickerButtonClass}
          >
            <span>
              {selectedCity?.name ??
                (!stateId ? "ابتدا استان را انتخاب کنید" : "انتخاب شهر")}
            </span>
            <Icon name="chevron-left" size={18} className="text-default-400" />
          </button>
        </Field>
      </Section>

      <Section title="اطلاعات تکمیلی">
        <Field label="تاریخ تولد">
          <DatePickerDrawer
            name="birthdate"
            value={birthdate}
            onSubmit={setBirthdate}
            placeholder="انتخاب تاریخ تولد"
            inputClassName={`${pickerButtonClass} text-right`}
            iconName="chevron-left"
          />
        </Field>
        <RowDivider />
        <div className="px-4 pb-3 pt-2.5">
          <p className="pb-1.5 text-xxs md:text-sm text-default-500" dir="rtl">
            جنسیت
          </p>
          <div
            role="radiogroup"
            dir="rtl"
            className="flex items-center gap-1.5 rounded-2xl bg-content2 p-1"
          >
            {GENDER_OPTIONS.map((opt) => {
              const selected = gender === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setGender(opt.value)}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-medium transition ${selected
                      ? "bg-primary text-white shadow-sm"
                      : "text-default-600 active:bg-content3"
                    }`}
                >
                  <Icon name={opt.icon} size={20} />
                  {opt.label}
                </button>
              );
            })}
          </div>
          {errors.gender ? (
            <p className="pt-1.5 text-xxs md:text-sm text-danger" dir="rtl">
              {errors.gender}
            </p>
          ) : null}
        </div>
      </Section>

      {submitLayout === "inline" ? (
        <div className="px-4 pt-4">
          <button
            type="button"
            onClick={onSubmit}
            disabled={saving}
            className="w-full h-11 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-60"
          >
            {saving ? savingLabel : submitLabel}
          </button>
        </div>
      ) : null}

      {submitLayout === "sticky" ? (
        <StickyActionButton
          label={saving ? savingLabel : submitLabel}
          onClick={onSubmit}
          disabled={saving}
        />
      ) : null}

      <PickerDrawer
        open={stateOpen}
        onOpenChange={setStateOpen}
        title="انتخاب استان"
        search={stateSearch}
        onSearchChange={setStateSearch}
        items={filteredStates}
        selectedId={stateId}
        onSelect={(id) => {
          setStateId(id);
          setCityId(0);
          setStateOpen(false);
        }}
      />

      <PickerDrawer
        open={cityOpen}
        onOpenChange={setCityOpen}
        title="انتخاب شهر"
        search={citySearch}
        onSearchChange={setCitySearch}
        items={filteredCities}
        selectedId={cityId}
        onSelect={(id) => {
          setCityId(id);
          setCityOpen(false);
        }}
      />
      <div className="safe-area-inset-bottom w-full"></div>

    </>
  );
}

export function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="px-4 py-2.5" dir="rtl">
      <label className="mb-0.5 block text-xxs md:text-sm text-default-500">{label}</label>
      {children}
      {error ? <p className="mt-1 text-xxs md:text-sm text-danger">{error}</p> : null}
    </div>
  );
}

type PickerItem = { id: number; name: string };

export function PickerDrawer({
  open,
  onOpenChange,
  title,
  search,
  onSearchChange,
  items,
  selectedId,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  search: string;
  onSearchChange: (value: string) => void;
  items: PickerItem[];
  selectedId: number;
  onSelect: (id: number) => void;
}) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[70vh]">
        <div className="space-y-3" dir="rtl">
          <h2 className="font-semibold text-sm text-default-800">{title}</h2>
          <div className="relative">
            <input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="جستجو"
              className={`${inputClass} pl-9`}
            />
            <span className="absolute top-1/2 left-3 -translate-y-1/2 text-default-400">
              <Icon name="search" size={18} />
            </span>
          </div>
          <div className="overflow-y-auto max-h-[50vh] divide-y divide-default-100">
            {items.length === 0 ? (
              <p className="text-center text-sm text-default-500 py-4">
                نتیجه‌ای پیدا نشد
              </p>
            ) : (
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onSelect(item.id)}
                  className="w-full flex items-center justify-between py-3 text-sm text-default-800"
                >
                  <span>{item.name}</span>
                  {item.id === selectedId ? (
                    <span className="text-primary text-xs">انتخاب شده</span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
